import { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { apiRequest, queryClient } from '@/lib/queryClient';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useToast } from '@/hooks/use-toast';
import {
  Mail,
  Send,
  User,
  Clock,
  Paperclip,
  Reply,
  ReplyAll,
  Forward,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  ArrowLeft,
} from 'lucide-react';
import { format, isToday, isYesterday, isThisWeek, differenceInDays } from 'date-fns';
// import { EmailSignatureSelector } from "./EmailSignatureSelector";

interface EmailMessage {
  id: number;
  conversationId: number;
  senderEmail: string;
  senderName?: string;
  recipientEmails: string[];
  ccEmails?: string[];
  subject: string;
  body: string;
  bodyText: string;
  bodyContent?: string;
  bodyContentType?: string;
  isRead: boolean;
  isOutbound: boolean;
  isSentBySystem?: boolean;
  receivedAt: string;
  sentAt?: string;
  hasAttachments: boolean;
}

interface EmailConversation {
  id: number;
  customerId: number;
  subject: string;
  participants: string[];
  messageCount: number;
  lastMessageAt: string;
  isRead: boolean;
  customerName?: string;
  customerEmail?: string;
}

interface EmailSignature {
  id: number;
  signatureName: string;
  signatureHtml: string;
  signatureText: string;
  isDefault: boolean;
  isActive: boolean;
}

interface EmailConversationViewProps {
  conversationId: number;
  customerId: number;
  onBack: () => void;
}

export function EmailConversationView({
  conversationId,
  customerId,
  onBack,
}: EmailConversationViewProps) {
  const { toast } = useToast();
  const [showReply, setShowReply] = useState(false);
  const [showForward, setShowForward] = useState(false);
  const [replyToAll, setReplyToAll] = useState(false);
  const [expandedMessages, setExpandedMessages] = useState<Set<number>>(new Set());
  const [replyData, setReplyData] = useState({
    body: '',
    signatureId: null as number | null,
  });
  const [forwardData, setForwardData] = useState({
    to: '',
    cc: '',
    body: '',
    signatureId: null as number | null,
  });

  // Fetch conversation details
  const { data: conversation, isLoading: conversationLoading } = useQuery({
    queryKey: ['/api/emails/conversation', conversationId],
    queryFn: () =>
      apiRequest<EmailConversation>('GET', `/api/emails/conversation/${conversationId}`),
    enabled: !!conversationId,
  });

  // Fetch messages for the conversation
  const { data: messages, isLoading: messagesLoading } = useQuery({
    queryKey: ['/api/emails/conversations', conversationId, 'messages'],
    queryFn: () =>
      apiRequest<EmailMessage[]>('GET', `/api/emails/conversations/${conversationId}/messages`),
    enabled: !!conversationId,
  });

  // Fetch email signatures
  const { data: signatures, isLoading: signaturesLoading } = useQuery({
    queryKey: ['/api/email-signatures'],
    queryFn: () => apiRequest<EmailSignature[]>('GET', '/api/email-signatures'),
  });

  // Reply to conversation mutation
  const replyMutation = useMutation({
    mutationFn: (replyBody: string) => {
      return apiRequest('POST', `/api/emails/conversations/${conversationId}/reply`, {
        body: replyBody,
        replyToAll,
        signatureId: replyData.signatureId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/emails/conversations'] });
      queryClient.invalidateQueries({
        queryKey: ['/api/emails/conversations', conversationId, 'messages'],
      });
      setShowReply(false);
      setReplyData({ body: '', signatureId: null });
      toast({
        title: 'Reply sent',
        description: 'Your reply has been sent successfully',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: `Failed to send reply: ${error.message}`,
        variant: 'destructive',
      });
    },
  });

  // Forward email mutation
  const forwardMutation = useMutation({
    mutationFn: (forwardBody: string) => {
      return apiRequest('POST', `/api/emails/conversations/${conversationId}/forward`, {
        to: forwardData.to,
        cc: forwardData.cc,
        body: forwardBody,
        signatureId: forwardData.signatureId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/emails/conversations'] });
      setShowForward(false);
      setForwardData({ to: '', cc: '', body: '', signatureId: null });
      toast({
        title: 'Email forwarded',
        description: 'Your email has been forwarded successfully',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: `Failed to forward email: ${error.message}`,
        variant: 'destructive',
      });
    },
  });

  // Mark as read mutation
  const markAsReadMutation = useMutation({
    mutationFn: (messageId: number) => {
      return apiRequest('POST', `/api/emails/messages/${messageId}/mark-read`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/emails/conversations'] });
      queryClient.invalidateQueries({
        queryKey: ['/api/emails/conversations', conversationId, 'messages'],
      });
    },
  });

  const handleSendReply = () => {
    if (!replyData.body.trim()) {
      toast({
        title: 'Missing message',
        description: 'Please enter a reply message',
        variant: 'destructive',
      });
      return;
    }

    replyMutation.mutate(replyData.body);
  };

  const handleSendForward = () => {
    if (!forwardData.to.trim() || !forwardData.body.trim()) {
      toast({
        title: 'Missing information',
        description: 'Please enter recipient and message',
        variant: 'destructive',
      });
      return;
    }

    forwardMutation.mutate(forwardData.body);
  };

  const handleReply = (message: EmailMessage, isReplyAll: boolean = false) => {
    setReplyToAll(isReplyAll);
    setShowReply(true);

    // Mark message as read
    if (!message.isRead) {
      markAsReadMutation.mutate(message.id);
    }
  };

  const handleForward = (message: EmailMessage) => {
    setShowForward(true);
    setForwardData((prev) => ({
      ...prev,
      body: `\n\n--- Forwarded Message ---\nFrom: ${message.senderEmail}\nTo: ${message.recipientEmails.join(', ')}\nSubject: ${message.subject}\nDate: ${format(new Date(message.receivedAt), 'PPpp')}\n\n${message.bodyText || message.body}`,
    }));
  };

  const toggleMessageExpansion = (messageId: number) => {
    const newExpanded = new Set(expandedMessages);
    if (newExpanded.has(messageId)) {
      newExpanded.delete(messageId);
    } else {
      newExpanded.add(messageId);
    }
    setExpandedMessages(newExpanded);
  };

  // Get current user info from session or context
  const currentUserEmail = 'office@domelec.co.uk'; // This should come from auth context

  // Hook for automatically fetching full email content for all messages
  const { mutate: loadFullContent } = useMutation({
    mutationFn: async (messageId: number) => {
      const response = await apiRequest({
        url: `/api/emails/messages/${messageId}/full-content`,
        method: 'GET',
      });
      if (!response.success) {
        throw new Error(response.note || 'Failed to load full content');
      }
      return response;
    },
    onSuccess: (data, messageId) => {
      // Update the messages query cache with full content
      queryClient.setQueryData(
        ['/api/emails/conversations', conversationId, 'messages'],
        (oldData: EmailMessage[] | undefined) => {
          if (!oldData) return oldData;
          return oldData.map((msg) =>
            msg.id === messageId
              ? {
                  ...msg,
                  bodyContent: data.fullContent,
                  bodyContentType: data.contentType,
                }
              : msg
          );
        }
      );
    },
    onError: (error) => {
      // Silent error handling - don't show toast for automatic loading failures
      console.log('Full content loading failed (using preview):', error.message);
    },
  });

  // Automatically load full content for messages that need it
  useEffect(() => {
    if (messages && messages.length > 0) {
      messages.forEach((message) => {
        const content = message.bodyContent || message.body || message.bodyText || '';
        // Load full content for messages with very short content (likely truncated previews)
        const needsFullContent = content.length < 200;

        if (needsFullContent) {
          // Automatically load full content for short preview messages
          loadFullContent(message.id);
        }
      });
    }
  }, [messages, loadFullContent]);

  const renderEmailContent = (message: EmailMessage) => {
    const content = message.bodyContent || message.body || message.bodyText || '';
    const isExpanded = expandedMessages.has(message.id);
    // Increased threshold for better readability
    const shouldShowMore = content.length > 1500;

    return (
      <div className="space-y-3">
        {/* Email Content Display - Improved formatting and visibility */}
        {message.bodyContentType === 'html' && content ? (
          <div
            className="email-content-html"
            dangerouslySetInnerHTML={{
              __html: shouldShowMore && !isExpanded ? content.substring(0, 1500) + '...' : content,
            }}
          />
        ) : (
          <div className="email-content-text">
            {shouldShowMore && !isExpanded
              ? content.substring(0, 1500) + '...'
              : content || 'No content available'}
          </div>
        )}

        {/* Expand/Collapse Button - Only for very long emails */}
        {shouldShowMore && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => toggleMessageExpansion(message.id)}
            className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 p-1 h-auto font-medium"
          >
            {isExpanded ? (
              <>
                <ChevronUp className="h-4 w-4 mr-1" />
                Show less
              </>
            ) : (
              <>
                <ChevronDown className="h-4 w-4 mr-1" />
                Show full email
              </>
            )}
          </Button>
        )}
      </div>
    );
  };

  if (conversationLoading || messagesLoading) {
    return (
      <Card className="h-full">
        <CardContent className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
            <div className="h-20 bg-gray-200 dark:bg-gray-700 rounded"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!conversation || !messages) {
    return (
      <Card className="h-full">
        <CardContent className="p-6">
          <div className="text-center">
            <Mail className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <p className="text-gray-500">Conversation not found</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <Card className="mb-4">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={onBack} className="p-2">
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div>
                <CardTitle className="text-lg">{conversation.subject}</CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  {conversation.participants.join(', ')} • {messages.length} messages
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleReply(messages[0])}
                className="flex items-center gap-2"
              >
                <Reply className="h-4 w-4" />
                Reply
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleReply(messages[0], true)}
                className="flex items-center gap-2"
              >
                <ReplyAll className="h-4 w-4" />
                Reply All
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleForward(messages[0])}
                className="flex items-center gap-2"
              >
                <Forward className="h-4 w-4" />
                Forward
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Messages */}
      <div className="flex-1 overflow-hidden">
        <ScrollArea className="h-full">
          <div className="space-y-4 p-4 max-w-5xl mx-auto">
            {messages.map((message, index) => {
              const messageDate = new Date(message.receivedAt);
              const prevMessage = index > 0 ? messages[index - 1] : null;
              const prevDate = prevMessage ? new Date(prevMessage.receivedAt) : null;

              // Determine if we should show a date separator
              let showDateSeparator = false;
              let dateLabel = '';

              if (!prevDate || differenceInDays(messageDate, prevDate) >= 1) {
                showDateSeparator = true;
                if (isToday(messageDate)) {
                  dateLabel = 'Today';
                } else if (isYesterday(messageDate)) {
                  dateLabel = 'Yesterday';
                } else if (isThisWeek(messageDate)) {
                  dateLabel = format(messageDate, 'EEEE');
                } else {
                  dateLabel = format(messageDate, 'MMMM d, yyyy');
                }
              }

              return (
                <div key={message.id}>
                  {/* Date Separator */}
                  {showDateSeparator && (
                    <div className="flex items-center gap-4 my-4">
                      <div className="flex-1 h-px bg-border"></div>
                      <span className="text-xs text-muted-foreground font-medium px-2">
                        {dateLabel}
                      </span>
                      <div className="flex-1 h-px bg-border"></div>
                    </div>
                  )}

                  {/* Message Card */}
                  <Card
                className={`${
                  !message.isRead && !message.isOutbound ? 'ring-2 ring-blue-500/20' : ''
                } ${
                  message.isOutbound
                    ? 'ml-8 bg-blue-50/50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-900'
                    : 'mr-8'
                }`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      {/* Avatar with sender initials */}
                      <div className={`flex items-center justify-center w-10 h-10 rounded-full font-semibold text-white ${
                        message.isOutbound
                          ? 'bg-blue-500 dark:bg-blue-600'
                          : 'bg-gray-500 dark:bg-gray-600'
                      }`}>
                        {message.senderName || message.senderEmail
                          ? ((message.senderName || message.senderEmail).split(' ')
                              .map(n => n[0])
                              .slice(0, 2)
                              .join('')
                              .toUpperCase() ||
                             (message.senderEmail?.substring(0, 2).toUpperCase()))
                          : 'UN'
                        }
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">
                            {message.senderName || message.senderEmail}
                            {message.isOutbound && message.senderName && (
                              <span className="text-sm text-muted-foreground ml-1">
                                (You)
                              </span>
                            )}
                          </span>
                          {message.isOutbound && (
                            <Badge variant="secondary" className="text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300">
                              Sent
                            </Badge>
                          )}
                          {!message.isRead && !message.isOutbound && (
                            <Badge variant="default" className="text-xs">
                              Unread
                            </Badge>
                          )}
                        </div>
                        <div className="text-sm text-muted-foreground mt-1">
                          {message.isOutbound ? (
                            <>
                              <span>To: {message.recipientEmails.join(', ')}</span>
                              {message.ccEmails && message.ccEmails.length > 0 && (
                                <span className="ml-2">CC: {message.ccEmails.join(', ')}</span>
                              )}
                            </>
                          ) : (
                            <>
                              <span>To: {message.recipientEmails.includes(currentUserEmail) ? 'You' : message.recipientEmails.join(', ')}</span>
                              {message.ccEmails && message.ccEmails.length > 0 && (
                                <span className="ml-2">CC: {message.ccEmails.join(', ')}</span>
                              )}
                            </>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                          <Clock className="h-3 w-3" />
                          <span>{format(new Date(message.receivedAt), 'PPpp')}</span>
                          {message.hasAttachments && (
                            <>
                              <Paperclip className="h-3 w-3 ml-2" />
                              <span>Has attachments</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleReply(message)}
                        className="p-2"
                      >
                        <Reply className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleReply(message, true)}
                        className="p-2"
                      >
                        <ReplyAll className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleForward(message)}
                        className="p-2"
                      >
                        <Forward className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="px-6 py-4">{renderEmailContent(message)}</CardContent>
                  </Card>
                </div>
              );
            })}
          </div>
        </ScrollArea>
      </div>

      {/* Reply Dialog */}
      <Dialog open={showReply} onOpenChange={setShowReply}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {replyToAll ? 'Reply All' : 'Reply'} to: {conversation.subject}
            </DialogTitle>
            <DialogDescription className="space-y-1">
              <div>
                {replyToAll
                  ? `Replying to all participants: ${conversation.participants.join(', ')}`
                  : `Replying to: ${conversation.participants[0]}`}
              </div>
              <div className="text-xs text-muted-foreground">
                Sending as: office@domelec.co.uk
              </div>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Sender Info */}
            <div className="p-3 bg-muted/50 rounded-lg flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white text-sm font-semibold">
                You
              </div>
              <div className="flex-1">
                <div className="text-sm font-medium">Your Reply</div>
                <div className="text-xs text-muted-foreground">From: office@domelec.co.uk</div>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium">Message</label>
              <Textarea
                placeholder="Type your reply..."
                value={replyData.body}
                onChange={(e) => setReplyData((prev) => ({ ...prev, body: e.target.value }))}
                className="min-h-[200px] mt-1"
              />
            </div>
            {/* EmailSignatureSelector temporarily disabled */}
            <div className="text-sm text-muted-foreground">
              Email signature selector coming soon
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReply(false)}>
              Cancel
            </Button>
            <Button onClick={handleSendReply} disabled={replyMutation.isPending}>
              {replyMutation.isPending ? 'Sending...' : 'Send Reply'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Forward Dialog */}
      <Dialog open={showForward} onOpenChange={setShowForward}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Forward: {conversation.subject}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">To</label>
              <Textarea
                placeholder="recipient@example.com"
                value={forwardData.to}
                onChange={(e) => setForwardData((prev) => ({ ...prev, to: e.target.value }))}
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-sm font-medium">CC (optional)</label>
              <Textarea
                placeholder="cc@example.com"
                value={forwardData.cc}
                onChange={(e) => setForwardData((prev) => ({ ...prev, cc: e.target.value }))}
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Message</label>
              <Textarea
                placeholder="Add your message..."
                value={forwardData.body}
                onChange={(e) => setForwardData((prev) => ({ ...prev, body: e.target.value }))}
                className="min-h-[200px] mt-1"
              />
            </div>
            {/* EmailSignatureSelector temporarily disabled */}
            <div className="text-sm text-muted-foreground">
              Email signature selector coming soon
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForward(false)}>
              Cancel
            </Button>
            <Button onClick={handleSendForward} disabled={forwardMutation.isPending}>
              {forwardMutation.isPending ? 'Forwarding...' : 'Forward'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
